import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Col, Row, message, Typography, Skeleton, Table, notification } from "antd";

import { getAssetsDeleted, getAssetsDeletedByCode } from "../../../../API/assets";

import WithPermission from "../../../Common/WithPermission/WithPermission";
import ExportButton from '../../../Common/ExportButton/ExportButton';
import BackButton from "../../../Common/BackButton/BackButton";
import BreadCrumb from "../../../Common/BreadCrumb/BreadCrumb";
import PageTitle from "../../../Common/PageTitle/PageTitle";
import ChangeLog from "../../../Common/ChangeLog/ChangeLog";
import AssetSearch from "../AssetSearch/AssetSearch";
import Columns from "./columns";

import { useAuth } from "../../../Context/AuthContext";

const AssetsDeleted = () => {
  const navigate = useNavigate();
  // Permisos
  const { hasPermission, userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Notificaciones
  const [api, contextHolder] = notification.useNotification();
  // Data
  const [assetsDeleted, setAssetsDeleted] = useState(null);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState(''); 

  useEffect(() => {
    const fetchAssetsDeleted = async () => {
      try {
        const data = await getAssetsDeleted();
        setAssetsDeleted(data);
        setFilteredData(data);
      } catch (error) {
        message.error("No se encontraron los activos eliminados.");
      }
    };

    fetchAssetsDeleted();
  }, []);

  // Obtener filtros desde la URL (si vienen desde el dashboard o la vista original)
  const [searchParams] = useSearchParams();
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];

  const filterParams = new URLSearchParams();
  if (assetsFromUrl.length) filterParams.set('assets', assetsFromUrl.join(','));
  if (areasFromUrl.length) filterParams.set('areas', areasFromUrl.join(','));

  const breadCrumbUrl = [
    { label: "Activos", url: "/assets", search: filterParams.toString() ? `?${filterParams.toString()}` : '' },
    { label: "Eliminados" },
  ];

  const goToBioFunctions = () => {
    navigate(-1);
  };

  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const evaluation = await getAssetsDeletedByCode(record.code);
      setChangeLogData(record);
      setChangeLogLogs(evaluation.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  const assetDeletedTableFilter = () => {
    const uniqueAssets = Array.from(
      new Set(assetsDeleted.map(a => a.name).filter(Boolean))).map(name => ({ name }));

    const uniqueUsers = Array.from(
      new Set(assetsDeleted.map(a => a.user?.username).filter(Boolean))).map(username => ({ username })); 

    const uniqueAreas = Array.from(
      new Set(assetsDeleted.map(a => a.area?.name).filter(Boolean))).map(name => ({ name }));
  
    return { 
      assets: uniqueAssets,
      users: uniqueUsers, 
      areas: uniqueAreas,
      dashboardAssetsFilter: assetsFromUrl ?? [],
      dashboardAreasFilter: areasFromUrl ?? [],
    };
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      //hasAnyRole={hasAnyRole}
      requiredPermission="read"
      //currentPermission={canAssetsRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div className="button-container">
          <Row justify="space-between">
            <Col span={18}>
              <PageTitle title="Activos Eliminados" icon="assets" />
            </Col>
          </Row>

          <Row justify="start" align="top">
            <Col span={18} style={{ display: "flex", alignItems: "center" }}>
              <BackButton onConfirm={goToBioFunctions} />
              <BreadCrumb location={breadCrumbUrl} />
            </Col>
            <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <AssetSearch
                assetsData={assetsDeleted}
                searchText={searchText}
                setSearchText={setSearchText}
                setFilteredData={setFilteredData}
              />
            </Col>
          </Row>

          <Row justify="end">
            <Col span={6} offset={18} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ExportButton
                api={api}
                contextHolder={contextHolder}
                data={filteredData}
                filename="Activos Eliminados.csv"
                transformData={(data) =>
                  data.map(item => ({
                    Nombre: item.name,
                    Descripción: item.description,
                    Responsable: item.area?.name || 'N/A',
                    "Creado por": item.user?.username || 'N/A',
                  }))
                }
              />
            </Col>
          </Row>
        </div>
      {
        !assetsDeleted ? (
        <>
          <Skeleton active paragraph={{ rows: 6 }} />
          <div style={{ height: 32 }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
          </Row>
        </>
        ) : (
          <>
            <Table
              className="custom-pagination deleted-table "
              columns={Columns(handleOpenChangeLog, assetDeletedTableFilter())}
              dataSource={filteredData} 
              rowKey="code"
              scroll={{ y: 450 }}
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
            />

            <ChangeLog
              open={changeLogVisible}
              onClose={handleCloseChangeLog}
              data={changeLogData}
              logs={changeLogLogs}
            />
          </>
        )
      }
      </>
    </WithPermission>
  );
};

export default AssetsDeleted;